import {
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  Skeleton,
  Tooltip,
  Input,
  Button,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Select,
  Textarea,
  useToast,
  Spinner,
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import {
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import { useDispatch, useSelector } from "react-redux";
import { fetchSubCompanies } from "views/admin/partnerDetail/services/subCompanies/action";
import { RootReducer } from "RootReducer";

// Custom components
import Card from "components/card/Card";

// Assets
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css"; // theme css file
import {
  NumberWithCommas,
  toReadableName,
  truncateString,
} from "../../../../utils";
import { Post } from "api";
import { MONTHS, YEARS } from "views/admin/partnerDetail/variables/dateValues";

export default function ColumnsTable(props: {
  columnsData: any;
  tableData: any;
  isLoading: boolean;
  partnerId: string | number;
  setPartnerData: React.Dispatch<React.SetStateAction<any>>;
}) {
  const { columnsData, tableData, isLoading, partnerId, setPartnerData } =
    props;

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isCreatingDeduction, setCreatingDeduction] = useState(false);
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>("");

  const dispatch = useDispatch();
  const { companies, fetchCompaniesRequestState } = useSelector(
    (state: RootReducer) => state.subCompanies
  );

  useEffect(() => {
    dispatch(fetchSubCompanies(partnerId));
  }, [dispatch, partnerId]);

  const toast = useToast();

  const tableInstance = useTable(
    {
      columns: columnsData,
      data: tableData,
      initialState: { pageSize: 200 },
    },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    initialState,
  } = tableInstance;
  initialState.pageSize = 200;

  useEffect(() => {
    tableInstance.setPageSize(200);
  }, [tableData, columnsData, isLoading, tableInstance]);

  const onCreateNewDeduction = useCallback(async () => {
    try {
      setCreatingDeduction(true);
      const requestBody: any = {
        year: Number(year),
        month: Number(month),
        amount: Number(amount),
        notes,
      };

      if (selectedCompanyId) {
        requestBody.partnerCompanyId = Number(selectedCompanyId);
      }

      const response = await Post(
        `/manage/partners/${partnerId}/deductions`,
        requestBody
      );

      if (response?.err) {
        toast({
          title: "Error",
          description: "Deduction creation failed. Please try again.",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      } else {
        onClose();

        setYear("");
        setMonth("");
        setAmount("");
        setNotes("");
        setSelectedCompanyId("");

        setPartnerData((prevData: any) => {
          const foundDeductionIndex = prevData.deductions.findIndex(
            (deduction: any) =>
              deduction.year === Number(year) &&
              deduction.month === Number(month)
          );

          if (foundDeductionIndex !== -1) {
            prevData.deductions[foundDeductionIndex] = {
              year: Number(year),
              month: Number(month),
              value: Number(amount),
              notes,
              redeemedAt: new Date().toISOString(),
            };

            toast({
              title: "Success",
              description: "Deduction updated successfully.",
              status: "success",
              duration: 5000,
              isClosable: true,
            });

            return {
              ...prevData,
              deductions: [...prevData.deductions],
            };
          }

          toast({
            title: "Success",
            description: "Deduction created successfully.",
            status: "success",
            duration: 5000,
            isClosable: true,
          });

          return {
            ...prevData,
            deductions: [
              {
                year: Number(year),
                month: Number(month),
                value: Number(amount),
                notes,
                redeemedAt: new Date().toISOString(),
              },
              ...prevData.deductions,
            ],
          };
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setCreatingDeduction(false);
    }
  }, [year, month, amount, notes, partnerId]);

  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "scroll" }}
    >
      <Flex px="25px" justify="space-between" mb="10px" align="center">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
          flex={5}
        >
          Deductions
        </Text>
        <Flex flex={2} justifyContent={"flex-end"}>
          <Button
            onClick={() => {
              onOpen();
            }}
            variant={"outline"}
            px={8}
            backgroundColor="brand.500"
            color={"white"}
            _hover={{ backgroundColor: "brand.600" }}
          >
            New Deduction
          </Button>
        </Flex>
      </Flex>
      {!tableData || (Array.isArray(tableData) && tableData.length === 0) ? (
        <Text
          ml={"25px"}
          {...getTableProps()}
          variant="striped"
          color="gray.500"
        >
          No deduction found.
        </Text>
      ) : (
        <Table
          {...getTableProps()}
          variant="striped"
          color="gray.500"
          mb="24px"
        >
          <Thead>
            {headerGroups.map((headerGroup, index) => (
              <Tr {...headerGroup.getHeaderGroupProps()} key={index}>
                {headerGroup.headers.map((column, index) => (
                  <Th
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                    pe="10px"
                    key={index}
                    borderColor={borderColor}
                  >
                    <Flex
                      justify="space-between"
                      align="center"
                      fontSize={{ sm: "10px", lg: "12px" }}
                      color="gray.400"
                    >
                      {column.render("Header")}
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()} key={"deduction-table-" + index}>
                  {row.cells.map((cell, index) => {
                    let data;
                    if (cell.column.Header === "NAME") {
                      data = (
                        <div>
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="700"
                          >
                            {cell.value}
                          </Text>
                        </div>
                      );
                    } else if (cell.column.Header === "VALUE") {
                      data = (
                        <Text color={textColor}>
                          ${NumberWithCommas(Number(cell.value).toFixed(2))}
                        </Text>
                      );
                    } else if (cell.column.Header === "REDEEMED AT") {
                      data = (
                        <Text color={textColor}>
                          {moment(cell.value).format("DD/MM/YYYY")}
                        </Text>
                      );
                    } else if (cell.column.Header === "NOTES") {
                      data =
                        typeof cell.value === "string" &&
                        cell.value.length < 25 ? (
                          <Text
                            color={textColor}
                            fontSize="sm"
                            fontWeight="500"
                          >
                            {cell.value || "-"}
                          </Text>
                        ) : (
                          <Tooltip label={cell.value} aria-label="A tooltip">
                            <Text
                              color={textColor}
                              fontSize="sm"
                              fontWeight="500"
                            >
                              {truncateString(cell.value)}
                            </Text>
                          </Tooltip>
                        );
                    } else {
                      data = (
                        <Text color={textColor} fontSize="sm" fontWeight="500">
                          {cell.value || "-"}
                        </Text>
                      );
                    }
                    return (
                      <Td
                        {...cell.getCellProps()}
                        key={index}
                        fontSize={{ sm: "14px" }}
                        maxH="30px !important"
                        py="8px"
                        minW={{ sm: "150px", md: "200px", lg: "2px" }}
                        borderColor="transparent"
                      >
                        <Skeleton
                          borderRadius={16}
                          endColor={"rgba(53,63,255,0.17)"}
                          isLoaded={!isLoading}
                        >
                          {data}
                        </Skeleton>
                      </Td>
                    );
                  })}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Deduction</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Flex flexDirection="column">
              <Flex flexDirection="row">
                <Flex flex={1} flexDirection="column" pr={2}>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Year
                  </Text>
                  <Select
                    placeholder="Year"
                    value={year}
                    onChange={(e) => setYear(e.target.value)}
                  >
                    {YEARS.map((year, index) => (
                      <option key={"year" + index} value={year}>
                        {year}
                      </option>
                    ))}
                  </Select>
                </Flex>
                <Flex flex={1} flexDirection="column" pl={2}>
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Month
                  </Text>
                  <Select
                    placeholder="Month"
                    value={month}
                    onChange={(e) => setMonth(e.target.value)}
                  >
                    {MONTHS.map((month, index) => (
                      <option key={"month" + index} value={month}>
                        {month}
                      </option>
                    ))}
                  </Select>
                </Flex>
              </Flex>
              <Flex flexDirection="row" mt={4}>
                <Flex flex={1} flexDirection="column">
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Amount
                  </Text>
                  <Input
                    placeholder="Amount"
                    value={amount}
                    type="number"
                    onChange={(e) => setAmount(e.target.value)}
                  />
                </Flex>
              </Flex>
              <Flex flexDirection="row" mt={4}>
                <Flex flex={1} flexDirection="column">
                  <Text fontSize="sm" color="gray.500" mb={2}>
                    Sub Company Name (Optional)
                  </Text>
                  {fetchCompaniesRequestState.isLoading ? (
                    <Spinner alignSelf={"center"} justifySelf="center" />
                  ) : (
                    <Select
                      placeholder="Select company"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                      isDisabled={fetchCompaniesRequestState.isLoading}
                    >
                      <Spinner alignSelf={"center"} justifySelf="center" />

                      {Array.isArray(companies) &&
                        companies.map((company) => (
                          <option key={company.id} value={company.id}>
                            {company.companyName}
                          </option>
                        ))}
                    </Select>
                  )}
                </Flex>
              </Flex>
              <Flex flex={1} flexDirection="column">
                <Text fontSize="sm" color="gray.500" mb={2}>
                  Notes
                </Text>
                <Textarea
                  placeholder="Notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Flex>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={onClose}
              variant="outline"
            >
              Close
            </Button>
            <Button
              variant="solid"
              bgColor={"brand.500"}
              color="white"
              _hover={{
                backgroundColor: "brand.600",
              }}
              onClick={onCreateNewDeduction}
              isLoading={isCreatingDeduction}
              isDisabled={!year || !month || !amount || isCreatingDeduction}
            >
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Card>
  );
}
